import { useMemo, useState } from "react";
import {
  AlertCircle,
  CalendarDays,
  Clock,
  Lightbulb,
  TrendingUp,
  Users,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ContentCard } from "@/components/ui/ContentCard";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import {
  computePatternAnalytics,
  TIME_OF_DAY_LABELS,
  type PatternInsight,
  type TimeOfDayBucket,
} from "./incidentPatternAnalytics";
import { formatGradeForDoc, type Incident } from "./types";

type InsightsScope = "mine" | "school";

type Props = {
  incidents: Incident[];
  schoolIncidents: Incident[];
  schoolId: string | null;
};

const timeChartConfig = {
  count: { label: "Incidents", color: "#248f84" },
};

const dayChartConfig = {
  count: { label: "Incidents", color: "#e8804c" },
};

const trendChartConfig = {
  count: { label: "Incidents", color: "#248f84" },
};

const shortenTimeLabel = (key: TimeOfDayBucket) => {
  const full = TIME_OF_DAY_LABELS[key];
  return full
    .replace("Morning (before 11 AM)", "Morning")
    .replace("Lunch period (11 AM – 1 PM)", "Lunch")
    .replace("Afternoon (1 – 3 PM)", "Afternoon")
    .replace("After school (after 3 PM)", "After school");
};

const insightIcon = (type: PatternInsight["type"]) => {
  switch (type) {
    case "time":
      return Clock;
    case "day":
      return CalendarDays;
    case "student":
    case "student_day":
      return Users;
    default:
      return Lightbulb;
  }
};

type ChartSectionProps = {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  className?: string;
  children: React.ReactNode;
};

const ChartSection = ({ title, subtitle, icon: Icon, className, children }: ChartSectionProps) => (
  <div className={cn("bg-gray-50 rounded-xl p-4 border border-gray-100", className)}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-bodhi-orange" />
      <span className="text-sm font-semibold text-gray-700">{title}</span>
    </div>
    <p className="text-xs text-gray-500 mb-4">{subtitle}</p>
    {children}
  </div>
);


export const IncidentPatternsDashboard = ({
  incidents,
  schoolIncidents,
  schoolId,
}: Props) => {
  const [scope, setScope] = useState<InsightsScope>("mine");

  const sourceIncidents = useMemo(() => {
    if (scope === "school") {
      const merged = new Map<string, Incident>();
      for (const inc of incidents) merged.set(inc.id, inc);
      for (const inc of schoolIncidents) merged.set(inc.id, inc);
      return [...merged.values()];
    }
    return incidents;
  }, [scope, incidents, schoolIncidents]);

  const analytics = useMemo(
    () => computePatternAnalytics(sourceIncidents),
    [sourceIncidents],
  );

  const maxTimeCount = Math.max(...analytics.timeOfDay.map((e) => e.count), 0);
  const maxDayCount = Math.max(...analytics.dayOfWeek.map((e) => e.count), 0);

  const timeChartData = analytics.timeOfDay.map((entry) => ({
    label: shortenTimeLabel(entry.key as TimeOfDayBucket),
    count: entry.count,
    fill: entry.count === maxTimeCount && entry.count > 0 ? "#248f84" : "#94d4cc",
  }));

  const dayChartData = analytics.dayOfWeek.map((entry) => ({
    label: entry.label.slice(0, 3),
    count: entry.count,
    fill: entry.count === maxDayCount && entry.count > 0 ? "#e8804c" : "#f5c4a8",
  }));

  if (analytics.totalIncidents === 0) {
    return (
      <ContentCard>
        <div className="py-10 text-center">
          <Lightbulb className="w-10 h-10 text-bodhi-teal/40 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-[#248f84] font-literata mb-2">
            Pattern insights will appear here
          </h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            As you log incidents with student names, this dashboard will highlight time-of-day trends,
            weekly rhythms, and students who may need additional support.
          </p>
        </div>
      </ContentCard>
    );
  }

  return (
    <ContentCard className="mb-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl lg:text-2xl font-semibold text-[#248f84] font-literata">
            Pattern Insights
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Surface trends in when and who — so you can adjust environments, schedules, and student
            support before patterns escalate.
          </p>
        </div>
        {schoolId && (
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit flex-shrink-0">
            <button
              type="button"
              onClick={() => setScope("mine")}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                scope === "mine"
                  ? "bg-white text-bodhi-teal shadow-sm"
                  : "text-gray-600 hover:text-gray-800",
              )}
            >
              My incidents
            </button>
            <button
              type="button"
              onClick={() => setScope("school")}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                scope === "school"
                  ? "bg-white text-bodhi-teal shadow-sm"
                  : "text-gray-600 hover:text-gray-800",
              )}
            >
              Whole school
            </button>
          </div>
        )}
      </div>

      {!analytics.hasEnoughData && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-100 p-4 mb-6">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Building your pattern picture</p>
            <p className="text-xs text-amber-700 mt-1">
              Log at least 3 incidents with student names to unlock trend insights. You have{" "}
              {analytics.totalIncidents} so far.
            </p>
          </div>
        </div>
      )}

      {analytics.insights.length > 0 && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-bodhi-gold" />
            Patterns to explore
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {analytics.insights.map((insight) => {
              const Icon = insightIcon(insight.type);
              return (
                <div
                  key={insight.id}
                  className="flex gap-3 rounded-xl bg-bodhi-teal/5 border border-bodhi-teal/10 p-4"
                >
                  <Icon className="w-4 h-4 text-bodhi-teal flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{insight.title}</p>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{insight.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChartSection
          title="Time of day"
          subtitle="When incidents are logged — useful for staffing and supervision"
          icon={Clock}
        >
          <ChartContainer config={timeChartConfig} className="h-[220px] w-full aspect-auto">
            <BarChart data={timeChartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </ChartSection>

        <ChartSection
          title="Day of week"
          subtitle="Spot weekly rhythms — e.g., Monday transitions or Friday fatigue"
          icon={CalendarDays}
        >
          <ChartContainer config={dayChartConfig} className="h-[220px] w-full aspect-auto">
            <BarChart data={dayChartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </ChartSection>
      </div>

      {analytics.weeklyTrend.length > 1 && (
        <ChartSection
          title="Weekly trend"
          subtitle="Incident volume over recent weeks"
          icon={TrendingUp}
          className="mb-6"
        >
          <ChartContainer config={trendChartConfig} className="h-[180px] w-full aspect-auto">
            <BarChart
              data={analytics.weeklyTrend}
              margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="weekLabel" tickLine={false} axisLine={false} fontSize={11} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="count" fill="#248f84" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </ChartSection>
      )}

      {analytics.repeatStudents.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-bodhi-teal" />
            Students appearing more than once
          </h3>
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3 font-medium">Student</th>
                  <th className="px-4 py-3 font-medium">Grade</th>
                  <th className="px-4 py-3 font-medium">Incidents</th>
                  <th className="px-4 py-3 font-medium">Pattern</th>
                  <th className="px-4 py-3 font-medium">Last incident</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {analytics.repeatStudents.map((student) => (
                  <tr key={student.studentKey} className="hover:bg-gray-50/80">
                    <td className="px-4 py-3 font-medium text-gray-800">{student.displayName}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {student.gradeLevel ? formatGradeForDoc(student.gradeLevel) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center min-w-[1.5rem] px-2 py-0.5 rounded-full bg-bodhi-teal/10 text-bodhi-teal text-xs font-semibold">
                        {student.incidentCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {student.dominantDay
                        ? `Often on ${student.dominantDay}s`
                        : student.repeatFlagCount > 0
                          ? "Flagged as repeat"
                          : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {student.lastIncidentDate
                        ? new Date(student.lastIncidentDate).toLocaleDateString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Open individual incidents from the My Incidents tab to review details and plan support.
          </p>
        </section>
      )}
    </ContentCard>
  );
};
